import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings as SettingsIcon, LogOut } from "lucide-react";
import { AuthApi, BridgeApi, OfficeApi, SpritesApi } from "../lib/api";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { Button } from "../components/primitives";
import { MountsEditor } from "../components/MountsEditor";

export default function Settings() {
  const toast = useToast();
  const { data: state, refetch } = useQuery({ queryKey: ["auth", "state"], queryFn: AuthApi.state });

  const [publicUrl, setPublicUrl] = useState("");
  useEffect(() => { if (state?.public_url) setPublicUrl(state.public_url); }, [state?.public_url]);

  const [savingUrl, setSavingUrl] = useState(false);
  async function saveUrl() {
    try { new URL(publicUrl); } catch { return toast.error("Public URL must be a valid URL"); }
    setSavingUrl(true);
    try {
      await AuthApi.setPublicUrl(publicUrl);
      toast.success("Public URL saved");
      refetch();
    } catch (err) {
      toast.error("Save failed", String(err));
    } finally { setSavingUrl(false); }
  }

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  async function changePw() {
    if (newPw.length < 6) return toast.error("New password must be ≥ 6 characters");
    if (newPw !== confirmPw) return toast.error("New passwords don't match");
    setSavingPw(true);
    try {
      await AuthApi.changePassword(currentPw, newPw);
      toast.success("Password changed — other sessions signed out");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error("Password change failed", String(err));
    } finally { setSavingPw(false); }
  }

  async function logout() {
    if (!confirm("Sign out of this session?")) return;
    try {
      await AuthApi.logout();
      window.location.reload();
    } catch (err) {
      toast.error("Logout failed", String(err));
    }
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Settings"
        icon={SettingsIcon}
        subtitle="Public URL and admin password"
        actions={
          <Button size="sm" variant="ghost" onClick={logout}>
            <LogOut className="size-3.5" />
            Sign out
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-6 max-w-2xl">
        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Public URL</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              The hostname your reverse proxy points at — drives the webhook URLs surfaced in the Channels page.
              Change this if you move the UI to a different domain. Existing webhook URLs in Google Cloud Console / external services need to be updated to match.
            </p>
          </header>
          <input
            value={publicUrl}
            onChange={(e) => setPublicUrl(e.target.value)}
            className="w-full text-sm font-mono"
            placeholder="https://agents.nge.pet"
          />
          <div>
            <Button size="sm" onClick={saveUrl} loading={savingUrl} disabled={publicUrl === state?.public_url}>
              Save URL
            </Button>
          </div>
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">OpenAI API key (memory embeddings)</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              Used to embed agent memories for semantic recall. Stored server-side in a 0600 file, never
              shown back in full. <strong>Leave empty to disable embeddings</strong> — memory then runs
              lexical-only (keyword recall), no vectors.
            </p>
          </header>
          <OpenAIKey />
          <div className="pt-3 border-t border-border">
            <div className="text-xs font-medium text-fg mb-0.5">Reflection model</div>
            <p className="text-[11px] text-fg-subtle mb-2">
              Model the nightly memory <strong>reflection</strong> uses to distil recent episodes into
              durable lessons. Any OpenAI chat model id (e.g. <code className="font-mono">gpt-5.4-mini</code>,
              {" "}<code className="font-mono">gpt-4.1-mini</code>). Embeddings always use
              {" "}<code className="font-mono">text-embedding-3-small</code> (separate).
            </p>
            <ReflectModel />
          </div>
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Character sprites (Visualize)</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              How the "Generate character" button on the Personas page draws a persona's pixel-art
              sprite for the Visualize office scene. Uses the OpenAI key above (images API,
              transparent background). Generated PNGs are downscaled to the grid size below.
            </p>
          </header>
          <SpriteSettings />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Office simulation (Visualize)</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              When a persona counts as active in the office. Spawned within the <em>wander</em> window →
              strolls around; within the <em>rest</em> window → naps on the couch/bench (limited spots);
              older → leaves the office. Applies live — no restart.
            </p>
          </header>
          <OfficeSettings />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Memory tuning</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              How the fleet remembers &amp; forgets. Read live by the bridge — changes apply on the
              next maintenance sweep / spawn, no restart. Leave a field at its default unless you
              know you want to change it.
            </p>
          </header>
          <MemoryTuning />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Inbound file size limit</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              Largest file a chat can send that the bridge copies into a persona's workdir for it to
              open (photos, PDFs, docs, audio). Bigger files are skipped from the copy — audio is still
              transcribed regardless. Read live by the bridge; changes apply on the next spawn, no restart.
            </p>
          </header>
          <InboundFileLimit />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Observer sweep interval</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              How often silent <em>observer</em> routes wake to ingest new messages into the persona's
              memory (and propose assist help, if configured). They never reply, so this can be long —
              a shorter interval reacts faster but spawns more often. Read live by the bridge; applies on
              the next sweep, no restart. Default 30 min.
            </p>
          </header>
          <ObserverSweep />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">RPC request deadline</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              How long an agent-to-agent <code>send_request</code> stays open before it auto-expires
              and the caller is un-stuck. Applies to both <em>sync</em> and <em>async</em> requests, and
              is refreshed on each reply hop so an active back-and-forth never expires mid-conversation.
              A per-call <code>deadline_s</code> still overrides this. (The in-turn <em>sync</em> block via
              <code> await_reply</code> is separately capped at ~45s by the HTTP transport.) Read live by
              the bridge; applies on the next request, no restart. Default 60 min.
            </p>
          </header>
          <RpcDeadline />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Assist sessions (broker relay)</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              When on, an <em>assist</em> observer hands its finding to a <em>broker</em> persona that relays it
              to you for a yes / no / <em>re-investigate</em> decision (tagged with a short token you reply to,
              e.g. <code>a yes</code>), then acts on approval. This also lets an observer report to a
              <em> different</em> persona's chat (e.g. atlas observes → alfred relays in your DM). Off ⇒ the
              legacy flow where the observing persona proposes and acts in its own report chat with a plain
              "yes". Read live by the bridge; applies on the next sweep, no restart.
            </p>
          </header>
          <AssistSessions />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Global sandbox mounts</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              Fleet-wide baseline mounts every sandboxed agent gets. Agent- and persona-level mounts
              layer on top (global → agent → persona; the most specific wins). Use <code className="font-mono">void</code> at
              a more specific tier to cancel one of these.
            </p>
          </header>
          <GlobalMounts />
        </section>

        <section className="panel p-4 space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-fg">Admin password</h2>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              Changing the password signs every other active session out of the panel — they'll be prompted to log in again.
            </p>
          </header>
          <label className="block">
            <span className="block text-xs text-fg-muted mb-1">Current password</span>
            <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full text-sm" autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="block text-xs text-fg-muted mb-1">New password</span>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full text-sm" autoComplete="new-password" />
          </label>
          <label className="block">
            <span className="block text-xs text-fg-muted mb-1">Confirm new password</span>
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full text-sm" autoComplete="new-password" />
          </label>
          <div>
            <Button size="sm" onClick={changePw} loading={savingPw} disabled={!currentPw || !newPw || !confirmPw}>
              Change password
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

function OpenAIKey() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["openai-key"], queryFn: () => AuthApi.openaiKey() });
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  async function save(value: string) {
    setSaving(true);
    try {
      const r = await AuthApi.setOpenaiKey(value);
      toast.success(r.set ? "OpenAI key saved" : "OpenAI key cleared — embeddings disabled");
      setKey("");
      qc.invalidateQueries({ queryKey: ["openai-key"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-fg-muted">
        {data?.set
          ? <>Currently set: <code className="font-mono text-fg">{data.hint}</code> — embeddings <span className="text-success">enabled</span>.</>
          : <>Not set — embeddings <span className="text-warning">disabled</span> (lexical recall only).</>}
      </div>
      <input
        type="password"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        className="w-full text-sm font-mono"
        placeholder="sk-proj-…"
        autoComplete="off"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => save(key.trim())} loading={saving} disabled={!key.trim().startsWith("sk-")}>Save key</Button>
        {data?.set && <Button size="sm" variant="ghost" onClick={() => save("")} disabled={saving}>Clear</Button>}
      </div>
    </div>
  );
}

function ReflectModel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["reflect-model"], queryFn: () => AuthApi.reflectModel() });
  const [model, setModel] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.model) setModel(data.model); }, [data?.model]);
  const SUGGEST = ["gpt-5.4-mini", "gpt-5-mini", "gpt-4.1-mini", "gpt-4o-mini", "o4-mini", "o3-mini"];
  async function save() {
    setSaving(true);
    try {
      const r = await AuthApi.setReflectModel(model.trim());
      toast.success(`Reflection model set to ${r.model}`);
      qc.invalidateQueries({ queryKey: ["reflect-model"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex items-center gap-2">
      <input
        list="reflect-models"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        className="flex-1 text-sm font-mono"
        placeholder={data?.default ?? "gpt-5.4-mini"}
      />
      <datalist id="reflect-models">{SUGGEST.map((m) => <option key={m} value={m} />)}</datalist>
      <Button size="sm" onClick={save} loading={saving} disabled={!model.trim() || model.trim() === data?.model}>Save</Button>
      {data && model.trim() !== data.default && (
        <Button size="sm" variant="ghost" onClick={() => { setModel(data.default); }} disabled={saving}>Reset</Button>
      )}
    </div>
  );
}

function InboundFileLimit() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["inbound-max"], queryFn: () => AuthApi.inboundMax() });
  const [mb, setMb] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.mb) setMb(String(data.mb)); }, [data?.mb]);
  async function save() {
    const v = Number(mb);
    if (!Number.isFinite(v) || v < 1 || v > 500) { toast.error("Invalid size", "Enter a number between 1 and 500 MB"); return; }
    setSaving(true);
    try {
      const r = await AuthApi.setInboundMax(v);
      toast.success(`Inbound file limit set to ${r.mb} MB`);
      qc.invalidateQueries({ queryKey: ["inbound-max"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min={1} max={500} value={mb}
        onChange={(e) => setMb(e.target.value)}
        className="w-24 text-sm" placeholder={String(data?.default ?? 25)}
      />
      <span className="text-xs text-fg-muted">MB</span>
      <Button size="sm" onClick={save} loading={saving} disabled={!mb || Number(mb) === data?.mb}>Save</Button>
      {data && Number(mb) !== data.default && (
        <Button size="sm" variant="ghost" onClick={() => setMb(String(data.default))} disabled={saving}>Reset</Button>
      )}
    </div>
  );
}

function ObserverSweep() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["observer-sweep"], queryFn: () => AuthApi.observerSweep() });
  const [min, setMin] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.minutes) setMin(String(data.minutes)); }, [data?.minutes]);
  async function save() {
    const v = Number(min);
    if (!Number.isFinite(v) || v < 1 || v > 1440) { toast.error("Invalid interval", "Enter a number between 1 and 1440 minutes"); return; }
    setSaving(true);
    try {
      const r = await AuthApi.setObserverSweep(v);
      toast.success(`Observer sweep set to ${r.minutes} min`);
      qc.invalidateQueries({ queryKey: ["observer-sweep"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min={1} max={1440} value={min}
        onChange={(e) => setMin(e.target.value)}
        className="w-24 text-sm" placeholder={String(data?.default ?? 30)}
      />
      <span className="text-xs text-fg-muted">minutes</span>
      <Button size="sm" onClick={save} loading={saving} disabled={!min || Number(min) === data?.minutes}>Save</Button>
      {data && Number(min) !== data.default && (
        <Button size="sm" variant="ghost" onClick={() => setMin(String(data.default))} disabled={saving}>Reset</Button>
      )}
    </div>
  );
}

function RpcDeadline() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["rpc-deadline"], queryFn: () => AuthApi.rpcDeadline() });
  const [min, setMin] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.minutes) setMin(String(data.minutes)); }, [data?.minutes]);
  async function save() {
    const v = Number(min);
    if (!Number.isFinite(v) || v < 1 || v > 1440) { toast.error("Invalid deadline", "Enter a number between 1 and 1440 minutes"); return; }
    setSaving(true);
    try {
      const r = await AuthApi.setRpcDeadline(v);
      toast.success(`RPC request deadline set to ${r.minutes} min`);
      qc.invalidateQueries({ queryKey: ["rpc-deadline"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min={1} max={1440} value={min}
        onChange={(e) => setMin(e.target.value)}
        className="w-24 text-sm" placeholder={String(data?.default ?? 60)}
      />
      <span className="text-xs text-fg-muted">minutes</span>
      <Button size="sm" onClick={save} loading={saving} disabled={!min || Number(min) === data?.minutes}>Save</Button>
      {data && Number(min) !== data.default && (
        <Button size="sm" variant="ghost" onClick={() => setMin(String(data.default))} disabled={saving}>Reset</Button>
      )}
    </div>
  );
}

function AssistSessions() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["assist-sessions"], queryFn: () => AuthApi.assistSessions() });
  const { data: maxData } = useQuery({ queryKey: ["assist-max-open"], queryFn: () => AuthApi.assistMaxOpen() });
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState("");
  useEffect(() => { if (maxData?.max) setMax(String(maxData.max)); }, [maxData?.max]);
  const enabled = data?.enabled ?? false;
  async function toggle(next: boolean) {
    setSaving(true);
    try {
      const r = await AuthApi.setAssistSessions(next);
      toast.success(`Assist sessions ${r.enabled ? "enabled" : "disabled"}`);
      qc.invalidateQueries({ queryKey: ["assist-sessions"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  async function saveMax() {
    const n = Number(max);
    if (!Number.isInteger(n) || n < 1 || n > 10) { toast.error("Invalid cap", "Enter an integer between 1 and 10"); return; }
    setSaving(true);
    try {
      const r = await AuthApi.setAssistMaxOpen(n);
      toast.success(`Open-session cap set to ${r.max}`);
      qc.invalidateQueries({ queryKey: ["assist-max-open"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-fg">
        <input
          type="checkbox" className="size-3.5"
          checked={enabled} disabled={saving || data === undefined}
          onChange={(e) => toggle(e.target.checked)}
        />
        Use the broker-relay FSM for assist-observer routes
      </label>
      <div>
        <div className="text-xs font-medium text-fg mb-0.5">Open sessions per chat</div>
        <p className="text-[11px] text-fg-subtle mb-2">
          How many assist sessions one observer may have open for the SAME chat at once — distinct
          findings can run in parallel instead of waiting for your decision on the first. 1 = the old
          one-at-a-time behavior. Applies on the next propose, no restart.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number" min={1} max={10} value={max}
            onChange={(e) => setMax(e.target.value)}
            className="w-24 text-sm" placeholder={String(maxData?.default ?? 3)}
          />
          <span className="text-xs text-fg-muted">sessions</span>
          <Button size="sm" onClick={saveMax} loading={saving} disabled={!max || Number(max) === maxData?.max}>Save</Button>
          {maxData && Number(max) !== maxData.default && (
            <Button size="sm" variant="ghost" onClick={() => setMax(String(maxData.default))} disabled={saving}>Reset</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SpriteSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["sprite-config"], queryFn: SpritesApi.config });
  const [cfg, setCfg] = useState<{ model: string; quality: string; size: number; style_prompt: string } | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (data) setCfg({ model: data.model, quality: data.quality, size: data.size, style_prompt: data.style_prompt });
  }, [data]);
  if (!cfg || !data) return <div className="text-xs text-fg-subtle">Loading…</div>;

  const MODELS = ["gpt-image-1-mini", "gpt-image-1", "gpt-image-1.5", "gpt-image-2"];
  async function save() {
    setSaving(true);
    try {
      const r = await SpritesApi.setConfig(cfg!);
      setCfg({ model: r.model, quality: r.quality, size: r.size, style_prompt: r.style_prompt });
      toast.success("Sprite settings saved");
      qc.invalidateQueries({ queryKey: ["sprite-config"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      {!data.key_set && (
        <div className="text-[11px] text-warning">No OpenAI key set — generation is disabled until one is saved above.</div>
      )}
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        <label className="block">
          <span className="block text-[11px] text-fg-muted mb-1">Model</span>
          <input list="sprite-models" value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })}
            className="w-full text-sm font-mono" placeholder={data.defaults.model} />
          <datalist id="sprite-models">{MODELS.map((m) => <option key={m} value={m} />)}</datalist>
          <span className="block text-[10px] text-fg-subtle mt-0.5">Any gpt-image model id.</span>
        </label>
        <label className="block">
          <span className="block text-[11px] text-fg-muted mb-1">Quality</span>
          <select value={cfg.quality} onChange={(e) => setCfg({ ...cfg, quality: e.target.value })} className="w-full text-sm">
            {["low", "medium", "high", "auto"].map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
          <span className="block text-[10px] text-fg-subtle mt-0.5">low is plenty for tiny sprites.</span>
        </label>
        <label className="block">
          <span className="block text-[11px] text-fg-muted mb-1">Grid size (px)</span>
          <input type="number" min={16} max={128} value={cfg.size}
            onChange={(e) => setCfg({ ...cfg, size: Number(e.target.value) || data.defaults.size })}
            className="w-full text-sm font-mono" />
          <span className="block text-[10px] text-fg-subtle mt-0.5">Downscale target — 32 matches the scene.</span>
        </label>
      </div>
      <label className="block">
        <span className="block text-[11px] text-fg-muted mb-1">Style prompt</span>
        <textarea rows={3} value={cfg.style_prompt} onChange={(e) => setCfg({ ...cfg, style_prompt: e.target.value })}
          className="w-full text-xs font-mono" />
        <span className="block text-[10px] text-fg-subtle mt-0.5">
          <code className="font-mono">{"{description}"}</code> is replaced by the keyword you type — or by the
          persona's own self-description when you leave the keyword empty.
        </span>
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save sprite settings</Button>
        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setCfg(data.defaults)}>Reset to defaults</Button>
      </div>
    </div>
  );
}

function OfficeSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["office-config"], queryFn: OfficeApi.config });
  const [cfg, setCfg] = useState<{ wander_min: number; rest_hours: number; interact_s: number } | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (data) setCfg({ wander_min: data.wander_min, rest_hours: data.rest_hours, interact_s: data.interact_s });
  }, [data]);
  if (!cfg || !data) return <div className="text-xs text-fg-subtle">Loading…</div>;

  async function save() {
    setSaving(true);
    try {
      const r = await OfficeApi.setConfig(cfg!);
      setCfg({ wander_min: r.wander_min, rest_hours: r.rest_hours, interact_s: r.interact_s });
      toast.success("Office settings saved — applies within seconds");
      qc.invalidateQueries({ queryKey: ["office-config"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }

  const field = (k: keyof typeof cfg, label: string, unit: string, hint: string) => (
    <label className="block">
      <span className="block text-[11px] text-fg-muted mb-1">{label}</span>
      <div className="flex items-center gap-1.5">
        <input type="number" min={1} value={cfg![k]}
          onChange={(e) => setCfg({ ...cfg!, [k]: Number(e.target.value) })}
          className="w-24 text-sm font-mono" />
        <span className="text-xs text-fg-muted">{unit}</span>
      </div>
      <span className="block text-[10px] text-fg-subtle mt-0.5">{hint}</span>
    </label>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        {field("wander_min", "Wander window", "min", "Spawned this recently → walks around the office.")}
        {field("rest_hours", "Rest window", "hours", "Older than wander but within this → naps. Beyond → goes home.")}
        {field("interact_s", "Chat window", "sec", "How long a bus message keeps two personas chatting.")}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save office settings</Button>
        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setCfg(data.defaults)}>Reset to defaults</Button>
      </div>
    </div>
  );
}

function MemoryTuning() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["memory-config"], queryFn: () => AuthApi.memoryConfig() });
  const [cfg, setCfg] = useState<import("../lib/api").MemoryConfig | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data?.config) setCfg(data.config); }, [data?.config]);
  if (!cfg) return <div className="text-xs text-fg-subtle">Loading…</div>;

  const set = (k: keyof typeof cfg, v: string | number) => setCfg({ ...cfg, [k]: v } as typeof cfg);
  const numField = (k: keyof typeof cfg, label: string, hint: string, step = 1) => (
    <label className="block">
      <span className="block text-[11px] text-fg-muted mb-1">{label}</span>
      <input type="number" step={step} value={cfg[k] as number}
        onChange={(e) => set(k, e.target.value === "" ? 0 : Number(e.target.value))}
        className="w-full text-sm font-mono" />
      <span className="block text-[10px] text-fg-subtle mt-0.5">{hint}</span>
    </label>
  );
  async function save() {
    setSaving(true);
    try {
      const r = await AuthApi.setMemoryConfig(cfg!);
      setCfg(r.config);
      toast.success("Memory settings saved");
      qc.invalidateQueries({ queryKey: ["memory-config"] });
    } catch (e) { toast.error("Save failed", String(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="block text-[11px] text-fg-muted mb-1">Embedding model</span>
        <input value={cfg.embedModel} onChange={(e) => set("embedModel", e.target.value)} className="w-full text-sm font-mono" placeholder={data?.defaults.embedModel} />
        <span className="block text-[10px] text-fg-subtle mt-0.5">OpenAI embedding model for semantic recall (changing it doesn't re-embed existing memories).</span>
      </label>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        {numField("shortTtlDays", "Short-term lifespan (days)", "Days a fact stays auto-injected before going long-term.")}
        {numField("eventTtlDays", "Episodic lifespan (days)", "Same, for one-off events (fade faster).")}
        {numField("coldFloor", "Forget threshold · facts", "Lower = keep longer before archiving.", 0.5)}
        {numField("eventColdFloor", "Forget threshold · episodes", "Higher = forget episodes sooner.", 0.5)}
        {numField("reflectMinEvents", "Reflection: min episodes", "Episodes needed before a reflection runs.")}
        {numField("reflectMaxEvents", "Reflection: max per run", "Episodes distilled in one reflection.")}
        {numField("maintenanceHours", "Maintenance interval (hours)", "How often the consolidate + reflect sweep runs.")}
        {numField("injectLimit", "Memories per spawn", "Recent/relevant memories injected into each spawn prompt.")}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save memory settings</Button>
        {data && <Button size="sm" variant="ghost" disabled={saving} onClick={() => setCfg(data.defaults)}>Reset to defaults</Button>}
      </div>
    </div>
  );
}

function GlobalMounts() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["global-mounts"], queryFn: () => BridgeApi.globalMounts() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["global-mounts"] });
  return (
    <MountsEditor
      own={data?.mounts ?? []}
      hint="global tier — every sandboxed agent inherits these"
      onAdd={(path, mode) => BridgeApi.addGlobalMount(path, mode).then(invalidate).catch((e) => toast.error("Add failed", String(e)))}
      onRemove={(path) => BridgeApi.removeGlobalMount(path).then(invalidate).catch((e) => toast.error("Remove failed", String(e)))}
    />
  );
}
